import { Injectable } from "@angular/core";
import { Observable, throwError, of } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map, catchError, retry, tap } from "rxjs/operators";
import { User } from "src/app/shared/models/user";
import { environment } from "src/environments/environment";
import _ from "lodash";
import { Helper } from "../shared/utils/helper";
import { SelectList } from "../shared/models/select-list";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private rootURL = environment.rootUrl;
  private baseUrl = environment.baseUrl;
  private tokenUrl = `${this.rootURL}/api/tokenauth/authenticate`;
  public refreshTokenUrl = `${this.rootURL}/api/tokenauth/refresh`;
  private token: string;

  constructor(private http: HttpClient) {}

  getToken(): string {
    return localStorage.getItem("access-token");
  }

  getRefreshToken(): string {
    return localStorage.getItem("refresh-token");
  }

  getUserId(): string {
    return localStorage.getItem("user-id");
  }

  refreshToken(): Observable<string> {
    let userid = this.getUserId();
    let refreshtoken = this.getRefreshToken();
    console.log("test ...");

    if (!refreshtoken || !userid) {
      this.logout();
      return of(null);
    }

    return this.http
      .post(this.refreshTokenUrl, {
        userId: Number(userid),
        refreshToken: refreshtoken,
      })
      .pipe(
        map((res) => this.extractToken(res)),
        catchError((e) => this.handleError(e))
      );
  }

  authenticate(
    username: string,
    password: string,
    rememberClient: boolean
  ): Observable<string> {
    return this.http
      .post(this.tokenUrl, {
        userNameOrEmailAddress: username,
        password: password,
        rememberClient: rememberClient,
      })
      .pipe(
        map((res) => this.extractToken(res)),
        catchError((e) => this.handleError(e))
      );
  }

  isAuthenticated() {
    return localStorage.getItem("access-token");
  }

  logout() {
    this.clear();
    const wnl = window.location;
    wnl.replace("#/login");
    wnl.reload();
  }

  handleError(e) {
    return throwError(e);
  }

  hasValidToken(): boolean {
    return !!this.getToken();
  }

  clear() {
    localStorage.clear();
  }

  extractToken(res: any): string {
    const result = res.result;
    localStorage.setItem("access-token", result.accessToken);
    localStorage.setItem("refresh-token", result.refreshToken);
    localStorage.setItem("encryptedAccessToken", result.encryptedAccessToken);
    localStorage.setItem("user-id", `${result.userId}`);
    const expireInSeconds = result.expireInSeconds;
    localStorage.setItem(
      "expiryIn",
      JSON.stringify({
        expireInSeconds: expireInSeconds,
        expiryTime: this.setExpiryTime(expireInSeconds),
      })
    );
    this.token = result.accessToken;
    localStorage.removeItem("refreshTokenStared");
    return this.token;
  }

  setExpiryTime(expirIn) {
    const exp = expirIn - 60;
    const currentTime = new Date().getTime();
    const loggedInTime = currentTime + exp * 1000;
    return loggedInTime;
  }

  isExpiryTimeExceeded(): boolean {
    try {
      const expiryIn = JSON.parse(localStorage.getItem("expiryIn"));
      const expiryTime = expiryIn ? expiryIn.expiryTime : null;
      if (expiryTime) {
        const currentTime = new Date().getTime();
        const hasExpired = currentTime > expiryTime;
        return hasExpired;
      }
      return true;
    } catch (e) {
      return true;
    }
  }

  saveUser(res: any) {
    const result = res.result;
    const user = Object.assign(new User(), result);
    localStorage.setItem("user", JSON.stringify(result));
    return user;
  }

  getUserDetails() {
    return this.http.get(`${this.baseUrl}/user/me/view`);
  }

  getBusiness(businessId) {
    return this.http.get(`${(this, this.baseUrl)}/business/view/${businessId}`);
  }

  loadUser(): User {
    const o = localStorage.getItem("user");
    let user = null;
    if (!_.isNull(o)) {
      user = JSON.parse(o);
    }

    return user;
  }

  loadUserPermissions(): string[] {
    const user = this.loadUser();
    if (user && user.permissions) {
      return user.permissions;
    }
    return [];
  }

  hasPermissions(permission: string) {
    const p = this.loadUserPermissions();
    return p.includes(permission);
  }

  getBusinessId() {
    const user = this.loadUser();
    let businessId = null;
    if (!_.isNull(user)) {
      businessId = user.businessId;
    }

    return businessId;
  }

  resetPassword(o) {
    return this.http.post(`${this.baseUrl}/v1/user/resetpassword`, o);
  }

  get_businessList() {
    if (!Helper.getUserObject("businessId")) {
      return of(null);
    }
    return this.http
      .get(
        `${this.baseUrl}/v1/business/selectlist/allowed?includeSelf=true&maxResultCount=10`
      )
      .pipe(retry(2));
  }
  get_searchBusinessList(term) {
    return this.http.get(
      `${this.baseUrl}/v1/business/selectlist/allowed?includeSelf=true&maxResultCount=10&query=${term}`
    );
  }

  getSelectListBusiness(query: string): Observable<Array<SelectList>> {
    const params = new HttpParams()
      .set("includeSelf", "false")
      .set("maxResultCount", "20")
      .set("useIsLastMile", "false")
      .set("Query", query);
    return this.http
      .get<{ result: Array<SelectList> }>(
        `${this.baseUrl}/business/selectlist/allowed`, {params}
      )
      .pipe(
        map((m) => [{text: `${Helper.getUserObject("businessId")} - ${Helper.getUserObject("businessName")}`, value: Helper.getUserObject("businessId")},...m.result]),
        tap((result)=>Helper.setBusinessSelectList([...result, ...Helper.getBusinessSelectList]))
    );
  }

  getLocalBusinessList(query=""): Observable<Array<SelectList>> {
    return Helper.getBusinessSelectList.length > 0? of(Helper.getBusinessSelectList): this.getSelectListBusiness(query);
  }
}
